import {
  DsSidebar,
  type SidebarEntry,
  type SidebarItem,
  type SidebarProfile,
  type SidebarSection,
  useScreenSize,
} from '@frontends/ds';
import { UiKitAlertDialog } from '@frontends/ui-kit';
import { useLocale } from '@frontends/util-i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import useAuth from '@/hooks/useAuth';
import { useEntityConfig } from '@/hooks/useEntityConfig';
import useIbDeskNavigation from '@/hooks/useIbDeskNavigation';
import useIsIbDeskAvailable from '@/hooks/useIsIbDeskAvailable';
import useLogout from '@/hooks/useLogout';
import { getInitials, replaceLanguageInUrl } from '@/lib/utils';
import { useBrandLogo } from './Logo';

interface AppSidebarProps {
  isMobileOpen: boolean;
  menuButtonId: string;
  onMobileOpenChange: (isOpen: boolean) => void;
}

export function AppSidebar({ menuButtonId, isMobileOpen, onMobileOpenChange }: AppSidebarProps) {
  const [isLogoutOpen, setLogoutOpen] = useState(false);
  const focusMenuButtonOnCloseRef = useRef(false);

  const { client, setOnboardingModalOpen } = useAuth();
  const { isDesktop } = useScreenSize();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const logoutMutation = useLogout();
  const config = useEntityConfig();
  const localeContext = useLocale();
  const ibNavigation = useIbDeskNavigation();

  const isIbDeskAvailable = useIsIbDeskAvailable();
  const fullLogo = useBrandLogo('full');
  const shortLogo = useBrandLogo('short');

  const clientDetails = client?.client_details;
  const isVerificationShown = config.IS_SHOW_ONBOARDING_DEBUG_MODE || !!clientDetails?.onboarding_steps?.length;

  const handleLogout = useCallback(() => {
    setLogoutOpen(false);
    logoutMutation.mutate();
  }, [logoutMutation]);

  useEffect(() => {
    if (isLogoutOpen || !focusMenuButtonOnCloseRef.current) {
      return;
    }

    focusMenuButtonOnCloseRef.current = false;
    document.getElementById(menuButtonId)?.focus();
  }, [isLogoutOpen, menuButtonId]);

  const ibDeskEntries: SidebarEntry[] = isIbDeskAvailable
    ? [{ id: 'ib-desk', icon: 'users', label: t('ib_desk'), badge: { label: t('beta') }, items: ibNavigation.items }]
    : [];

  const verificationItems: SidebarItem[] = isVerificationShown
    ? [
        {
          id: 'verification',
          icon: 'shield-check',
          label: t('verification'),
          onAction: () => setOnboardingModalOpen(true),
        },
      ]
    : [];

  const menuSections: SidebarSection[] = [
    {
      id: 'main',
      items: [{ id: 'services', icon: 'layout-grid', label: t('services'), href: '/' }, ...ibDeskEntries],
    },
    {
      id: 'account',
      items: [
        ...verificationItems,
        { id: 'sessions', icon: 'history', label: t('sessions'), href: '/sessions' },
        {
          id: 'security',
          icon: 'lock-keyhole',
          label: t('security'),
          href: replaceLanguageInUrl(config.VITE_SECURITY_URL, localeContext),
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        { id: 'accounts', icon: 'wallet', label: t('accounts'), href: '/accounts' },
      ],
    },
  ];

  const bottomItems: SidebarItem[] = [
    { id: 'logout', icon: 'log-out', label: t('logout'), onAction: () => setLogoutOpen(true) },
  ];

  const getProfile = (): SidebarProfile | undefined => {
    if (!clientDetails) {
      return undefined;
    }

    const getCaption = (): string => {
      if (!config.VITE_IB_DESK_ENABLED) {
        return t('profile_caption_id', { id: clientDetails.id });
      }

      if (isIbDeskAvailable) {
        return t('profile_caption_ib', { id: clientDetails.id });
      }

      return t('profile_caption_client', { id: clientDetails.id });
    };

    return {
      name: clientDetails.name.trim() || `${clientDetails.first_name} ${clientDetails.last_name}`.trim(),
      initials: getInitials(clientDetails),
      caption: getCaption(),
    };
  };

  return (
    <>
      <DsSidebar
        aria-label={t('main_menu')}
        sections={menuSections}
        bottomItems={bottomItems}
        currentPath={pathname}
        logo={fullLogo ? { fullLogoSrc: fullLogo.src, shortLogoSrc: shortLogo?.src, alt: fullLogo.alt } : undefined}
        profile={getProfile()}
        isMobileOpen={isMobileOpen}
        onMobileOpenChange={onMobileOpenChange}
      />

      <UiKitAlertDialog
        open={isLogoutOpen}
        onOpenChange={setLogoutOpen}
        title={t('logout')}
        description={t('ask_for_logout')}
        cancelLabel={t('cancel')}
        confirmLabel={t('confirm_logout')}
        confirmTone="primary"
        isDismissable={false}
        onCancel={() => {
          focusMenuButtonOnCloseRef.current = !isDesktop;
        }}
        onConfirm={handleLogout}
      />
    </>
  );
}

export default AppSidebar;
